import { useCallback, useEffect, useState } from 'react';
import {
  ensureTelegramReady,
  exchangeAuth,
  getAuthToken,
  isTokenExpiringSoon,
  refreshAuth,
  uploadClobCreds,
  type AuthExchangeResponse,
} from './api';
import { deriveClobCreds, getSigner } from './wallet';
import { DEV_PREVIEW } from './devPreview';

export type AuthStatus =
  | { state: 'loading' }
  | { state: 'error'; error: string; retry: () => void }
  | { state: 'ready'; auth: AuthExchangeResponse; address: string };

const REFRESH_WINDOW_MS = 5 * 60_000; // refresh when <5min remain
const REFRESH_TICK_MS = 60_000;

// Boot is brittle on the Telegram in-app browser (WKWebView): TLS handshakes,
// Web3Auth SDK fetches, and our own /auth/exchange occasionally surface as
// generic "Load failed" / "Failed to fetch" errors. Retry transparently
// before dead-ending the user on the error screen.
const BOOT_RETRY_DELAYS_MS = [400, 1200, 3000];
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

const DEV_PREVIEW_AUTH: AuthExchangeResponse = {
  token: 'dev-preview-token',
  expiresAt: new Date(Date.now() + 60 * 60_000).toISOString(),
  externalUserId: 'dev-preview',
  telegramUserId: '0',
  userExists: true,
  walletAddress: '0x000000000000000000000000000000000000dEaD',
  depositWalletAddress: '0x000000000000000000000000000000000000dEaD',
  language: 'pt',
  hasClobCreds: true,
};

/**
 * Boots the Mini App:
 *   1. Telegram.WebApp.ready/expand
 *   2. POST /webapp/auth/exchange (validates initData, mints JWT)
 *   3. Resolves the user's EOA via the wallet abstraction
 *   4. (Optional) re-calls exchange with walletAddress so the API can
 *      persist it once Web3Auth has resolved the key.
 *   5. Background tick that calls /webapp/auth/refresh before expiry.
 */
export function useAuth(): AuthStatus {
  const [status, setStatus] = useState<AuthStatus>({ state: 'loading' });
  const [bootEpoch, setBootEpoch] = useState(0);

  const retry = useCallback(() => {
    setStatus({ state: 'loading' });
    setBootEpoch((n) => n + 1);
  }, []);

  useEffect(() => {
    if (DEV_PREVIEW) {
      setStatus({
        state: 'ready',
        auth: DEV_PREVIEW_AUTH,
        address: DEV_PREVIEW_AUTH.walletAddress!,
      });
      return;
    }
    let cancelled = false;

    const boot = async () => {
      const tag = (step: string, err: unknown) => {
        const msg = (err as Error)?.message ?? String(err);
        const wrapped = new Error(`[${step}] ${msg}`);
        (wrapped as Error & { cause?: unknown }).cause = err;
        return wrapped;
      };

      try {
        ensureTelegramReady();
      } catch (err) {
        throw tag('telegram', err);
      }
      // Reuse cached JWT (set by api.ts on load) when still fresh.
      let auth: AuthExchangeResponse | null = null;
      if (getAuthToken() && !isTokenExpiringSoon(REFRESH_WINDOW_MS)) {
        try {
          auth = await refreshAuth();
        } catch {
          auth = null;
        }
      }
      if (!auth) {
        try {
          auth = await exchangeAuth();
        } catch (err) {
          throw tag('exchange', err);
        }
      }
      let signer;
      try {
        signer = await getSigner({ jwt: auth.token, subject: auth.externalUserId });
      } catch (err) {
        throw tag('signer', err);
      }
      if (auth.walletAddress?.toLowerCase() !== signer.address.toLowerCase()) {
        try {
          auth = await exchangeAuth(signer.address, signer.address, 0);
        } catch (err) {
          throw tag('exchange-wallet', err);
        }
      }
      // Derive L2 CLOB creds when backend says we don't have them yet.
      // Backend is the source of truth; localStorage was unreliable.
      if (!auth.hasClobCreds) {
        try {
          const creds = await deriveClobCreds(signer);
          await uploadClobCreds(creds);
          auth = { ...auth, hasClobCreds: true };
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('clob creds derivation failed', e);
        }
      }
      return { auth, address: signer.address };
    };

    (async () => {
      let lastErr: unknown = null;
      for (let attempt = 0; attempt <= BOOT_RETRY_DELAYS_MS.length; attempt++) {
        if (cancelled) return;
        try {
          const { auth, address } = await boot();
          if (cancelled) return;
          setStatus({ state: 'ready', auth, address });
          return;
        } catch (err) {
          lastErr = err;
          const delay = BOOT_RETRY_DELAYS_MS[attempt];
          if (delay == null) break;
          // eslint-disable-next-line no-console
          console.warn(`auth boot attempt ${attempt + 1} failed, retrying in ${delay}ms`, err);
          await sleep(delay);
        }
      }
      if (cancelled) return;
      setStatus({ state: 'error', error: (lastErr as Error).message, retry });
    })();
    return () => {
      cancelled = true;
    };
  }, [bootEpoch, retry]);

  // Background refresher.
  useEffect(() => {
    if (status.state !== 'ready') return;
    if (DEV_PREVIEW) return;
    const id = window.setInterval(() => {
      if (!isTokenExpiringSoon(REFRESH_WINDOW_MS)) return;
      refreshAuth()
        .then((auth) => setStatus((s) => (s.state === 'ready' ? { ...s, auth } : s)))
        .catch(() => {
          // swallow; the next API call will surface the auth error.
        });
    }, REFRESH_TICK_MS);
    return () => window.clearInterval(id);
  }, [status.state]);

  return status;
}
